<template>
  <LoadingWrapper :state="status">
    <div
      class="relative flex min-h-dvh w-full grow flex-col lg:h-dvh lg:min-h-0 lg:overflow-hidden"
    >
      <div
        class="bg-surface-container-low sticky top-0 z-30 flex h-20 shrink-0 items-center justify-between gap-4 px-2 py-4 md:px-6"
      >
        <div class="flex items-center">
          <BackButton
            backup-route-name="teaching-unit-running"
            :params="{ id: teachingUnitId }"
            title="Zurück"
            class="-ml-2"
          />
        </div>
        <div class="flex items-center gap-4">
          <BaseTextButton
            color="text"
            size="XS"
            icon="cloud_off"
            class="text-on-surface-variant! hidden md:flex"
            @click="openInfoDialog"
            >Lokal gespeichert</BaseTextButton
          >
          <BaseIconButton
            color="standard"
            size="S"
            icon="cloud_off"
            class="text-on-surface-variant! md:hidden"
            @click="openInfoDialog"
          />
          <BaseTextButton
            color="tonalPrimary"
            :size="{ base: 'S', lg: 'M' }"
            icon="save"
            @click="openDialog"
            >{{ $t('feedback.multiple.save') }}</BaseTextButton
          >
        </div>
      </div>

      <div
        class="bg-surface-container-low grid grow grid-cols-1 overflow-hidden lg:min-h-0 lg:grid-cols-7"
      >
        <div class="sticky top-0 z-10 col-span-2 overflow-y-auto p-4 pt-0 lg:static">
          <BaseHorizontalOverflow>
            <ul class="flex w-max flex-row lg:w-full lg:flex-col lg:gap-1">
              <li
                v-for="(tandem, index) in sortedReceivers"
                :key="index"
                class="bg-surface rounded-large overflow-hidden"
              >
                <FeedbackListItem
                  :member="tandem.receiver"
                  :total-questions="
                    data.questionGroups.flatMap((questiongroup) => questiongroup.questions.flat())
                      .length
                  "
                  :total-answers="
                    feedbacks.filter(
                      (feedback) =>
                        feedback.feedbackReceiverId === tandem.receiver.id &&
                        (feedback.text != undefined || feedback.value != undefined),
                    ).length
                  "
                  :is-active="currentTandem?.receiver.id === tandem.receiver.id"
                  :is-finished="true"
                  @click="selectReceiver(tandem.receiver.id)"
                />
              </li>
            </ul>
          </BaseHorizontalOverflow>
        </div>
        <div
          ref="scrollContainer"
          class="bg-surface rounded-extra-large col-span-5 mr-2 mb-2 overflow-y-auto"
        >
          <div v-if="currentTandem" class="col-span-5 flex w-full flex-col gap-8 p-8">
            <div class="flex flex-col gap-4 md:flex-row md:items-center">
              <div class="flex grow flex-row items-center gap-4">
                <BaseMemberAvatar
                  :key="currentTandem?.receiver.id"
                  :include-last-name="true"
                  :member="currentTandem?.receiver"
                  size="80"
                />
                <h1 class="text-headline-small md:text-display-small grow">
                  {{ memberFullName(currentTandem.receiver) }}
                </h1>
              </div>
              <FeedbackVisibilityInformation
                class="w-auto!"
                :receiver="currentTandem?.receiver"
                :supervisors="currentTandem?.supervisors"
              />
            </div>
            <ListQuestionsView
              :receiver-id="currentTandem?.receiver.id"
              :question-groups="data.questionGroups"
              :model-value="currentFeedback"
              @update:model-value="(value) => handleFeedbacks(value)"
            />
            <div class="mt-8 flex w-full justify-between">
              <div>
                <BaseTextButton
                  v-if="prevStudent"
                  color="text"
                  icon="arrow_back"
                  @click="goToPrev"
                  >{{ memberFullName(prevStudent.receiver) }}</BaseTextButton
                >
              </div>
              <div>
                <BaseTextButton
                  v-if="nextStudent"
                  color="text"
                  trailing-icon="arrow_forward"
                  @click="goToNext"
                  >{{ memberFullName(nextStudent.receiver) }}</BaseTextButton
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <BaseDialog
      :is-open="isInfoDialogOpen"
      type="basic"
      @close="closeInfoDialog"
      title="Änderungen werden nur lokal gespeichert"
      description="Deine Änderungen werden in diesem Browser gespeichert. Erst wenn du abschliesst, werden deine Feedbacks online gesichert."
    >
      <template #confirm>
        <BaseTextButton color="filled" @click="closeInfoDialog">
          {{ t('common.ok') }}
        </BaseTextButton>
      </template>
    </BaseDialog>
    <BaseDialog
      :is-open="isOpen"
      type="basic"
      @close="closeDialog"
      :title="t('feedback.multiple.saveDialog.title')"
      :description="t('feedback.multiple.saveDialog.description')"
    >
      <template #dismiss>
        <BaseTextButton color="text" @click="closeDialog">
          {{ t('common.cancel') }}
        </BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="filled" @click="storeFeedbacks">
          {{ $t('feedback.multiple.save') }}
        </BaseTextButton>
      </template>
    </BaseDialog>
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import BackButton from '@/core/components/BackButton.vue'
  import { useMultipleFeedback } from '../composables/useMultipleFeedback'
  import LoadingWrapper from '../../../core/components/loading/LoadingWrapper.vue'
  import { useRouteParams, useRouteQuery } from '@vueuse/router'
  import { computed, useTemplateRef, watchEffect } from 'vue'
  import BaseHorizontalOverflow from '@/core/components/base/horizontal-overflow/BaseHorizontalOverflow.vue'
  import FeedbackListItem from '../components/FeedbackListItem.vue'
  import type { JSONContent } from '@tiptap/vue-3'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useRouter } from 'vue-router'
  import { useLocalFeedbacks } from '@/core/composables/useLocalFeedbacks'
  import ListQuestionsView from '../components/list-questions-view/ListQuestionsView.vue'
  import usePost from '@/core/composables/api/usePost'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import { useSnackStore } from '@/stores/snack.store'
  import { useI18n } from 'vue-i18n'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import FeedbackVisibilityInformation from '../components/finish-screen/FeedbackVisibilityInformation.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import { compareMembersByName, memberFullName } from '@/core/models/member/member-name'

  const surveyId = useRouteParams<string>('surveyId')
  const teachingUnitId = useRouteParams<string>('id')

  const currentReceiverId = useRouteQuery<string>('receiverId', undefined, { mode: 'replace' })

  const router = useRouter()

  const { data, status } = useMultipleFeedback(surveyId.value)

  const { feedbacksBySurveyId } = useLocalFeedbacks()
  const scrollContainer = useTemplateRef<HTMLElement>('scrollContainer')

  const selectReceiver = (receiverId: string) => {
    currentReceiverId.value = receiverId
    scrollTop()
  }

  const scrollTop = () => {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTo({
        top: 0,
      })
    }
  }

  const { isOpen, openDialog, closeDialog } = useDialog()
  const {
    isOpen: isInfoDialogOpen,
    openDialog: openInfoDialog,
    closeDialog: closeInfoDialog,
  } = useDialog()
  const feedbacks = feedbacksBySurveyId(surveyId.value)

  const currentFeedback = computed(() => {
    return feedbacks.value.filter(
      (feedback) => feedback.feedbackReceiverId === currentReceiverId.value,
    )
  })

  const handleFeedbacks = (
    newFeedbacks: {
      value?: number
      text?: JSONContent
      questionId: string
      questionGroupId: string
    }[],
  ) => {
    feedbacks.value = [
      ...feedbacks.value.filter(
        (feedback) => feedback.feedbackReceiverId != currentReceiverId.value,
      ),
      ...newFeedbacks.map((feedback) => ({
        ...feedback,
        labelKeys: [],
        feedbackReceiverId: currentReceiverId.value,
      })),
    ]
  }

  interface GiveStructuredFeedbackToMultipleBody {
    readonly receiverFeedbacks: ReadonlyArray<{
      readonly receiverId: string
      readonly feedbacks: ReadonlyArray<{
        readonly questionId: string
        readonly value?: number
        readonly text?: JSONContent
        readonly questionGroupId: string
        readonly labelKeys?: string[]
      }>
    }>
  }

  const { mutate: saveFeedbacks, status: saveFeedbacksStatus } = usePost<
    string,
    GiveStructuredFeedbackToMultipleBody
  >('feedback/structured/' + surveyId.value)

  const snackStore = useSnackStore()
  const { t } = useI18n()

  const storeFeedbacks = async () => {
    await saveFeedbacks({
      receiverFeedbacks: data.value.tandems.map((tandem) => {
        const receiverId = tandem.receiver.id
        const actualFeedbacks = feedbacks.value.filter(
          (feedback) => feedback.feedbackReceiverId === receiverId,
        )
        return {
          receiverId: receiverId,
          feedbacks: actualFeedbacks.map((f) => ({
            questionId: f.questionId,
            value: f.value,
            text: f.text,
            questionGroupId: f.questionGroupId,
            labelKeys: f.labelKeys,
            subFeedbacks: f.subFeedbacks,
          })),
        }
      }),
    })

    switch (saveFeedbacksStatus.value) {
      case ApiRequestStatus.Success:
        snackStore.showInfo(t('feedback.giveFeedbackSuccess'))
        router.back()
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('feedback.giveFeedbackError'))
        break
    }
  }

  const currentTandem = computed(() => {
    if (data.value.tandems.length === 0) {
      return undefined
    }

    return data.value.tandems.find((tandem) => tandem.receiver.id == currentReceiverId.value)
  })

  watchEffect(() => {
    if (data.value?.tandems?.length > 0 && !currentReceiverId.value) {
      currentReceiverId.value = sortedReceivers.value[0].receiver.id
    }
  })

  const sortedReceivers = computed(() =>
    [...(data.value?.tandems || [])].sort((tandemA, tandemB) =>
      compareMembersByName(tandemA.receiver, tandemB.receiver),
    ),
  )

  const currentIndex = computed(() => {
    return sortedReceivers.value.findIndex(
      (tandem) => tandem.receiver.id === currentReceiverId.value,
    )
  })

  const prevStudent = computed(() => {
    if (sortedReceivers.value.length < 2) return undefined

    if (currentIndex.value > 0) {
      return sortedReceivers.value[currentIndex.value - 1]
    }

    if (sortedReceivers.value.length > 2) {
      return sortedReceivers.value[sortedReceivers.value.length - 1]
    }

    return undefined
  })

  const nextStudent = computed(() => {
    if (sortedReceivers.value.length < 2) return undefined

    if (currentIndex.value < sortedReceivers.value.length - 1) {
      return sortedReceivers.value[currentIndex.value + 1]
    }

    if (sortedReceivers.value.length > 2) {
      return sortedReceivers.value[0]
    }

    return undefined
  })

  const goToPrev = () => {
    if (prevStudent.value) {
      currentReceiverId.value = prevStudent.value.receiver.id
      scrollTop()
    }
  }

  const goToNext = () => {
    if (nextStudent.value) {
      currentReceiverId.value = nextStudent.value.receiver.id
      scrollTop()
    }
  }
</script>
